import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
  LineChart,
  Line,
} from "recharts";
import { BsBarChartFill, BsGraphUp } from "react-icons/bs";

const BASE = "https://eps.elkomprintama.com";
const toNum = (n) => {
  const v = Number(n);
  return isNaN(v) ? null : v;
};
const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const METRICS = [
  { key: "finish_qty", label: "Finish Qty", color: "#4f46e5" },
  { key: "order_qty", label: "Order Qty", color: "#0891b2" },
  { key: "job_count", label: "# Jobsheets", color: "#059669" },
  { key: "customer_count", label: "# Customers", color: "#d97706" },
];

const isCountMetric = (key) => key === "job_count" || key === "customer_count";

const fmtAxis = (v, asCount) => {
  if (asCount) return v;
  if (v >= 1_000_000) return `${+(v / 1_000_000).toFixed(1)}m`;
  if (v >= 1_000) return `${Math.round(v / 1_000)}k`;
  return v;
};

const fmtLabel = (v, asCount) => {
  if (!v) return "";
  return fmtAxis(v, asCount);
};

function MetricToggle({ value, onChange }) {
  return (
    <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs font-semibold">
      {METRICS.map((m) => (
        <button
          key={m.key}
          onClick={() => onChange(m.key)}
          className={`px-3 py-1.5 transition-colors ${
            value === m.key
              ? "text-white"
              : "text-gray-500 bg-white hover:bg-gray-50"
          }`}
          style={value === m.key ? { background: m.color } : {}}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

function BarTooltip({ active, payload, label, metric }) {
  if (!active || !payload?.length) return null;
  const m = METRICS.find((x) => x.key === metric);
  const v = payload[0]?.value ?? 0;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg px-4 py-3 text-sm">
      <p className="font-bold text-gray-700 mb-1">{label}</p>
      <p style={{ color: m.color }} className="font-semibold">
        {m.label}: {isCountMetric(metric) ? v : v.toLocaleString("en-US")}
      </p>
    </div>
  );
}

function LineTooltip({ active, payload, label, metric }) {
  if (!active || !payload?.length) return null;
  const m = METRICS.find((x) => x.key === metric);
  const v = payload[0]?.value ?? 0;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg px-4 py-3 text-sm">
      <p className="font-bold text-gray-700 mb-1">{label}</p>
      <p style={{ color: m.color }} className="font-semibold">
        {m.label}: {isCountMetric(metric) ? v : v.toLocaleString("en-US")}
      </p>
    </div>
  );
}

function YearlyChart() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [metric, setMetric] = useState("finish_qty");
  const [chartType, setChartType] = useState("bar");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--)
    years.push(y);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${BASE}/get/api/yearly-report`, { params: { year } })
      .then((res) => {
        const map = {};
        res.data.data.forEach((r) => {
          map[r.month_num] = r;
        });
        setData(
          MONTH_LABELS.map((label, i) => ({
            label,
            month: i + 1,
            finish_qty: toNum(map[i + 1]?.finish_qty) ?? 0,
            order_qty: toNum(map[i + 1]?.order_qty) ?? 0,
            job_count: Number(map[i + 1]?.job_count ?? 0),
            customer_count: Number(map[i + 1]?.customer_count ?? 0),
          })),
        );
      })
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [year]);

  const activeM = METRICS.find((m) => m.key === metric);
  const asCount = isCountMetric(metric);
  const total = data.reduce((a, r) => a + r[metric], 0);
  const peak = [...data].sort((a, b) => b[metric] - a[metric])[0];
  const activeMths = data.filter((r) => r[metric] > 0).length;
  const YAXIS_W = 52;

  const axisProps = {
    tick: { fontSize: 10, fill: "#9ca3af" },
    axisLine: false,
    tickLine: false,
    tickFormatter: (v) => fmtAxis(v, asCount),
    width: YAXIS_W,
  };

  return (
    <div
      className="flex flex-col gap-4"
      style={{ maxHeight: "calc(100dvh - 80px)", overflowY: "auto" }}
    >
      {/* Header */}
      <div className="w-full rounded-lg bg-indigo-700 flex-shrink-0">
        <h1 className="block uppercase text-white tracking-wide text-4xl text-center font-bold py-2">
          Yearly Chart
        </h1>
      </div>

      {/* Controls */}
      <div className="flex-shrink-0 bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4 flex flex-wrap items-end gap-5">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Year
          </label>
          <select
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Metric
          </label>
          <MetricToggle value={metric} onChange={setMetric} />
        </div>

        {/* Chart type tab */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Chart Type
          </label>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs font-semibold">
            <button
              onClick={() => setChartType("bar")}
              className={`flex items-center gap-1.5 px-4 py-2 transition-colors ${
                chartType === "bar"
                  ? "bg-indigo-600 text-white"
                  : "bg-white text-gray-500 hover:bg-gray-50"
              }`}
            >
              <BsBarChartFill size={12} /> Bar
            </button>
            <button
              onClick={() => setChartType("line")}
              className={`flex items-center gap-1.5 px-4 py-2 transition-colors ${
                chartType === "line"
                  ? "bg-indigo-600 text-white"
                  : "bg-white text-gray-500 hover:bg-gray-50"
              }`}
            >
              <BsGraphUp size={12} /> Line
            </button>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="flex-shrink-0 grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
            Total {activeM.label}
          </p>
          <p className="text-2xl font-bold" style={{ color: activeM.color }}>
            {asCount ? total : total.toLocaleString("en-US")}
          </p>
          <p className="text-xs text-gray-400">{year} full year</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
            Peak Month
          </p>
          <p className="text-2xl font-bold text-gray-800">
            {peak ? MONTH_LABELS[(peak.month ?? 1) - 1] : "—"}
          </p>
          <p className="text-xs text-gray-400">
            {peak
              ? asCount
                ? peak[metric]
                : (peak[metric] ?? 0).toLocaleString("en-US")
              : "—"}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
            Active Months
          </p>
          <p className="text-2xl font-bold text-gray-800">{activeMths}</p>
          <p className="text-xs text-gray-400">of 12 months</p>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 pt-5 pb-4">
        {loading ? (
          <div className="flex items-center justify-center h-80 text-gray-400 text-sm">
            Loading…
          </div>
        ) : chartType === "bar" ? (
          <ResponsiveContainer width="100%" height={360}>
            <BarChart
              data={data}
              margin={{ top: 24, right: 16, left: 0, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#e5e7eb"
              />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#6b7280" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis {...axisProps} />
              <Tooltip
                content={<BarTooltip metric={metric} />}
                cursor={{ fill: "#f3f4f6", radius: 4 }}
              />
              <Bar dataKey={metric} radius={[5, 5, 0, 0]} maxBarSize={52}>
                <LabelList
                  dataKey={metric}
                  position="top"
                  style={{ fontSize: 10, fill: "#6b7280", fontWeight: 600 }}
                  formatter={(v) => fmtLabel(v, asCount)}
                />
                {data.map((entry) => (
                  <Cell
                    key={entry.month}
                    fill={entry[metric] === 0 ? "#e5e7eb" : activeM.color}
                    opacity={entry[metric] === 0 ? 0.4 : 1}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height={360}>
            <LineChart
              data={data}
              margin={{ top: 16, right: 16, left: 0, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#e5e7eb"
              />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#6b7280" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis {...axisProps} />
              <Tooltip
                content={<LineTooltip metric={metric} />}
                cursor={{ stroke: "#e5e7eb" }}
              />
              <Line
                dataKey={metric}
                stroke={activeM.color}
                strokeWidth={2.5}
                dot={{ r: 5, fill: activeM.color, strokeWidth: 0 }}
                activeDot={{ r: 7, strokeWidth: 0 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

export default YearlyChart;
